/** Library (mockup v7): type chips, status tabs, filters, acronym-aware filter box, no horizontal scroll. */
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api, type DocView } from "../lib/api";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "../lib/store";
import { AREA_LABELS, AREA_CODES, CLASSIFICATION_CODES, CLASSIFICATION_LABELS, FRAMEWORKS, TYPE_COLORS, TYPE_LABELS, TYPE_NAMES, TYPE_CODES, coName } from "../lib/brand";
import { FrameworkTags } from "../components/FrameworkPicker";
import { daysUntil, fmt } from "../lib/format";
import { latestDate, pendKey } from "../lib/access";
import { Avatar, Co, Empty, Hl, Loading, StatusCell, TypeBadge } from "../components/bits";
import { Icon } from "../components/Icon";
import { timeOf, useSort } from "../components/Sort";
import { SeeHow } from "../components/SeeHow";

const TABS = ["all", "pub", "rev", "chg", "draft", "due", "archived"] as const;
type Tab = (typeof TABS)[number];
const TAB_LABEL: Record<Tab, string> = { all: "All", pub: "Published", rev: "In review", chg: "Changes requested", draft: "Drafts", due: "Review due", archived: "Show archived" };

/** A full-text snippet: the server wraps each hit in [[ ]] (ADR-0015). */
export function Snippet({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  text.split(/(\[\[.*?\]\])/).forEach((t, i) => {
    if (t.startsWith("[[") && t.endsWith("]]")) parts.push(<mark key={i}>{t.slice(2, -2)}</mark>);
    else if (t) parts.push(t);
  });
  return <span className="snip"><span className="src">In the file</span>…{parts}…</span>;
}

export function LibraryPage() {
  const { p, ws, docs, docsError, search, terms } = useStore();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = (TABS as readonly string[]).includes(sp.get("tab") ?? "") ? (sp.get("tab") as Tab) : "all";
  const type = sp.get("type") ?? "", area = sp.get("area") ?? "", co = sp.get("co") ?? "", q = sp.get("q") ?? "", fw = sp.get("fw") ?? "", cls = sp.get("cls") ?? "";
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v); else next.delete(k);
    setSp(next, { replace: true });
  };

  // Archived documents load on demand (authors, reviewers, approvers and admins only).
  const [archived, setArchived] = useState<DocView[] | null>(null);
  useEffect(() => {
    if (tab !== "archived" || archived) return;
    api.archivedDocuments().then((r) => setArchived(r.documents), () => setArchived([]));
  }, [tab, archived]);
  // Words inside the files: asked of the server after a short pause in typing.
  const [snips, setSnips] = useState<{ q: string; byId: Map<string, string> }>({ q: "", byId: new Map() });
  useEffect(() => {
    const term = q.trim();
    // Two-letter acronyms ("IR") count too: the server searches their meaning inside files.
    const isAcronym = term.split(/\s+/).some((w) => terms.some((t) => t.term.toLowerCase() === w.toLowerCase()));
    if ((term.length < 3 && !isAcronym) || tab === "archived") { setSnips({ q: "", byId: new Map() }); return; }
    let live = true;
    const t = setTimeout(() => {
      api.searchDocuments(term).then((r) => {
        if (live) setSnips({ q: term, byId: new Map(r.documents.filter((d) => d.snippet).map((d) => [d.id, d.snippet!])) });
      }, () => undefined);
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [q, tab, terms]);

  const inWs = (d: DocView) => d.company === "CPY" || ws === "ALL" || d.company === ws;
  const live = docs ?? [];
  const all = tab === "archived" ? (archived ?? []).filter(inWs) : live;
  const seeP = (code: string) => p.canSeePending(code);
  const due = (d: (typeof all)[number]) => { const n = daysUntil(d.review_date); return !!d.revision && n !== null && n <= 30; };
  const counts: Record<Tab, number> = useMemo(() => ({
    all: live.length,
    pub: live.filter((d) => d.revision).length,
    archived: 0,
    rev: live.filter((d) => pendKey(d) === "rev" && seeP(d.company)).length,
    chg: live.filter((d) => pendKey(d) === "chg" && seeP(d.company)).length,
    draft: live.filter((d) => pendKey(d) === "draft" && seeP(d.company)).length,
    due: live.filter(due).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [live]);

  // Framework filter options: the built-in list plus any custom tags in use.
  const fwOptions = useMemo(() => {
    const extra = new Set<string>();
    for (const d of all) for (const t of d.frameworks ?? []) if (!(FRAMEWORKS as readonly string[]).includes(t)) extra.add(t);
    return [...FRAMEWORKS, ...[...extra].sort()];
  }, [all]);
  const typesInUse = TYPE_CODES.filter((k) => k === type || live.some((d) => d.type === k)).concat(type && !TYPE_CODES.includes(type) ? [type] : []);
  let list = all.filter((d) => (!type || d.type === type) && (!area || d.area === area) && (!co || d.company === co) && (!cls || d.classification === cls)
    && (!fw || (d.frameworks ?? []).some((t) => t.toLowerCase() === fw.toLowerCase())));
  if (tab === "pub") list = list.filter((d) => d.revision);
  if (tab === "rev" || tab === "chg" || tab === "draft") list = list.filter((d) => pendKey(d) === tab && seeP(d.company));
  if (tab === "due") list = list.filter(due);
  const res = search(q, list);
  const titleRows = q ? res.rows : [...res.rows].sort((a, b) => latestDate(b.d).localeCompare(latestDate(a.d)));
  const textOn = snips.q && snips.q === q.trim();
  const seen = new Set(titleRows.map((r) => r.d.id));
  const rows: { d: DocView; why: string; snippet?: string }[] = [
    ...titleRows.map((r) => ({ ...r, snippet: textOn ? snips.byId.get(r.d.id) : undefined })),
    ...(textOn ? list.filter((d) => !seen.has(d.id) && snips.byId.has(d.id)).map((d) => ({ d, why: "words inside the file", snippet: snips.byId.get(d.id) })) : []),
  ];

  // Sortable (2026-10-26): best match first while searching, until a header is clicked.
  const { sorted: sortedRows, th } = useSort("vault.library", rows, { title: (r) => r.d.title, status: (r) => r.d.status, review: (r) => timeOf(r.d.review_date),
    owner: (r) => r.d.owner?.name ?? "" }, { key: "", dir: "asc" });
  const showPending = p.myCodes.some((c) => p.canSeePending(c));
  const tabs: Tab[] = (["all", "pub"] as Tab[]).concat(showPending ? ["rev", "chg", "draft"] : []).concat(["due"]).concat(showPending ? ["archived"] : []);
  const wsLabel = ws === "ALL" ? "all your companies" : coName(ws);
  const filtersSet = type || area || co || q || fw || cls;
  const onlyReader = p.primaryRoles.length === 1 && p.primaryRoles[0] === "reader";

  if (!docs) return <Loading label="Loading the library…" />;
  if (tab === "archived" && !archived) return <Loading label="Loading archived documents…" />;

  return (
    <div className="view">
      <div className="hgroup">
        <div>
          <div className="eyebrow">{wsLabel} · plus company-wide</div>
          <h1 className="title">Policies, procedures <em>&amp;</em> forms</h1>
          <div className="lede-help"><SeeHow video="find" label="See how to find a document fast" /></div>
        </div>
        <div className="chips">
          {/* Only the types in use (23+ types would crowd the page); every type is in the Type filter. */}
          {typesInUse.map((k) => (
            <button key={k} className={`chip ${type === k ? "on" : ""}`} onClick={() => set("type", type === k ? "" : k)} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              <i style={{ width: 8, height: 8, borderRadius: 2, background: TYPE_COLORS[k], display: "inline-block" }} />{TYPE_LABELS[k]}
            </button>
          ))}
        </div>
      </div>
      <div className="tabs" role="tablist">
        {tabs.map((k) => (
          <button key={k} className={`tab ${tab === k ? "on" : ""}`} role="tab" aria-selected={tab === k} onClick={() => set("tab", k === "all" ? "" : k)}>
            {TAB_LABEL[k]}{k !== "archived" && <span className="n" style={k === "chg" && counts.chg ? { background: "var(--bad)", color: "#fff" } : undefined}>{counts[k]}</span>}
          </button>
        ))}
      </div>
      <div className="filters">
        <label className="inline-search"><Icon name="search" size={16} />
          <input placeholder="Title, number, acronym or words in the file" value={q} onChange={(e) => set("q", e.target.value)} aria-label="Filter documents" />
        </label>
        {ws === "ALL" && (
          <select className={`fsel ${co ? "set" : ""}`} value={co} onChange={(e) => set("co", e.target.value)} aria-label="Company">
            <option value="">Company</option>
            {["CPY", ...p.myCodes].map((c) => <option key={c} value={c}>{coName(c)}</option>)}
          </select>
        )}
        <select className={`fsel ${area ? "set" : ""}`} value={area} onChange={(e) => set("area", e.target.value)} aria-label="Area">
          <option value="">Area</option>
          {AREA_CODES.map((k) => <option key={k} value={k}>{AREA_LABELS[k] ?? k}</option>)}
        </select>
        <select className={`fsel ${type ? "set" : ""}`} value={type} onChange={(e) => set("type", e.target.value)} aria-label="Type">
          <option value="">Type</option>
          {TYPE_CODES.map((k) => <option key={k} value={k}>{TYPE_NAMES[k] ?? k} ({k})</option>)}
        </select>
        <select className={`fsel ${cls ? "set" : ""}`} value={cls} onChange={(e) => set("cls", e.target.value)} aria-label="Classification">
          <option value="">Classification</option>
          {CLASSIFICATION_CODES.map((k) => <option key={k} value={k}>{CLASSIFICATION_LABELS[k] ?? k}</option>)}
        </select>
        <select className={`fsel ${fw ? "set" : ""}`} value={fw} onChange={(e) => set("fw", e.target.value)} aria-label="Framework">
          <option value="">Framework</option>
          {fwOptions.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        {res.expansions.map((e) => <span key={e.term} className="expand">{e.term.toUpperCase()} → {e.expansion}</span>)}
        {filtersSet && <button className="clear" onClick={() => setSp(tab === "all" ? {} : { tab }, { replace: true })}>Clear</button>}
      </div>
      {docsError && <div className="note bad">The library couldn't refresh: {docsError}</div>}
      <div className="card tbl-wrap">
        {rows.length ? (
          <table className="tbl dt">
            <colgroup><col /><col style={{ width: 200 }} /><col className="hide-md" style={{ width: 132 }} /><col className="hide-md" style={{ width: 62 }} /><col style={{ width: 58 }} /></colgroup>
            <thead><tr>{th("title", "Document")}{th("status", "Status")}{th("review", "Next review", "hide-md")}{th("owner", "Owner", "hide-md")}<th><span className="sr">View</span></th></tr></thead>
            <tbody>
              {sortedRows.map(({ d, why, snippet }) => {
                const n = daysUntil(d.review_date);
                const dueCls = n !== null && d.revision ? (n < 0 ? "over" : n <= 30 ? "soon" : "") : "";
                return (
                  <tr key={d.id} tabIndex={0} onClick={() => nav(`/doc/${d.id}`)} onKeyDown={(e) => { if (e.key === "Enter") nav(`/doc/${d.id}`); }}>
                    <td>
                      <div className="docrow">
                        <TypeBadge type={d.type} />
                        <div className="meta">
                          <div className="t1"><Hl text={d.title} q={q} expansions={res.expansions} /></div>
                          <div className="sub">
                            <span className="docno">{d.base_number}{d.version ? <span className="rev"> v{d.version}</span> : null}</span>
                            <Co code={d.company} />
                            {d.classification && d.classification !== "INT" && <span className={`cls cls-${d.classification}`}>{CLASSIFICATION_LABELS[d.classification] ?? d.classification}</span>}
                            <FrameworkTags tags={d.frameworks} max={3} />
                            {q && why ? <span style={{ color: "var(--accent-ink)", fontWeight: 600 }}>Matched: {why}</span>
                              : <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{d.description}</span>}
                          </div>
                          {snippet && <Snippet text={snippet} />}
                          {d.archived && <div className="snip"><span className="src">Archived {fmt(d.archived.at)}</span>{d.archived.by}{d.archived.reason ? ` · ${d.archived.reason}` : ""}</div>}
                        </div>
                      </div>
                    </td>
                    <td>{d.archived ? <span className="pill arch">Archived</span> : <StatusCell d={d} p={p} />}{d.checked_out && !d.archived && <div className="snip" title={`Checked out by ${d.checked_out.by.name}`}><span className="src">Checked out</span>{d.checked_out.by.name}</div>}</td>
                    <td className={`due hide-md ${dueCls}`}>
                      {d.revision && d.review_date ? (n !== null && n < 0 ? <>Overdue<br /><span style={{ fontWeight: 500 }}>{fmt(d.review_date)}</span></> : fmt(d.review_date)) : "-"}
                    </td>
                    <td className="hide-md"><Avatar id={d.owner.id} name={d.owner.name} size={26} /></td>
                    <td style={{ textAlign: "right" }}>
                      <button className="iconbtn" title="View in browser" aria-label={`View ${d.title}`} onClick={(e) => { e.stopPropagation(); nav(`/doc/${d.id}`); }}><Icon name="eye" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : tab === "archived" ? (
          <Empty title="Nothing archived">Admins can archive a document from its page. Archived documents are hidden from readers and keep their full history.</Empty>
        ) : all.length ? (
          <Empty title="No matches">
            {q ? <>Nothing matches “{q}”. </> : "Nothing matches these filters. "}
            {q && p.anyAdmin && <>Add it as an acronym in <Link to="/settings/acronyms">Settings</Link>?</>}
          </Empty>
        ) : (
          <Empty title="Nothing here yet">
            {p.anyAuthor ? <>Add the first one with <Link to="/new">New document</Link>. It publishes once an approver signs off.</> : "Documents appear once they are approved."}
          </Empty>
        )}
      </div>
      <div className="foot-note">
        {rows.length} of {all.length} shown{onlyReader ? " · readers see approved documents only" : ""}. Search understands acronyms and initials: try <b>IR</b>, <b>EDR</b>, <b>BCP</b> or a number like <b>SEC-001</b>.
      </div>
    </div>
  );
}
